/**
 * Guest Monkey slips: skip POST after sign-in if all matches have already ended.
 */

import type { PendingGuestMonkeyChallengePayload } from '../../shared/stores/pendingGuestMonkeyChallengeStore';
import type { Game } from '../types/create.types';
import { parseKickoffFromDisplay } from './createGames.helper';
import {
  GUEST_MONKEY_ESTIMATED_MATCH_DURATION_MS,
  GUEST_MONKEY_SLIP_TTL_MS,
} from '../constants/guestMonkeyMirage.constants';

/**
 * Latest estimated end time across all matches in the slip.
 * Returns (latest kickoff + GUEST_MONKEY_ESTIMATED_MATCH_DURATION_MS), or null if no kickoffs found.
 */
export function computeLatestEstimatedEndMsFromGames(
  matchIds: string[],
  games: Game[],
): number | null {
  let latest: number | null = null;
  const seen = new Set<string>();
  for (const id of matchIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    const game = games.find((g) => g.id === id);
    if (!game) continue;
    const kickoff = parseKickoffFromDisplay(game);
    if (!kickoff) continue;
    const ms = kickoff.getTime();
    if (latest == null || ms > latest) {
      latest = ms;
    }
  }
  return latest == null ? null : latest + GUEST_MONKEY_ESTIMATED_MATCH_DURATION_MS;
}

/**
 * True when the latest estimated match end has passed — do not POST this guest slip.
 * Payloads without {@link PendingGuestMonkeyChallengePayload.latestEstimatedEndMs} are not skipped (unknown / legacy).
 */
export function isPendingGuestMonkeyPayloadExpired(
  payload: Pick<PendingGuestMonkeyChallengePayload, 'latestEstimatedEndMs'>,
  nowMs: number = Date.now(),
): boolean {
  const end = payload.latestEstimatedEndMs;
  if (end == null) return false;
  return nowMs >= end;
}

/**
 * True when a slip has been sitting in the queue longer than GUEST_MONKEY_SLIP_TTL_MS (48 h).
 * Prevents orphaned slips from accumulating if a user never signs in after creating a challenge.
 * Payloads without {@link PendingGuestMonkeyChallengePayload.enqueuedAt} are not discarded (legacy).
 */
export function isPendingGuestMonkeyPayloadAbandoned(
  payload: Pick<PendingGuestMonkeyChallengePayload, 'enqueuedAt'>,
  nowMs: number = Date.now(),
): boolean {
  const enqueuedAt = payload.enqueuedAt;
  if (!enqueuedAt) return false;
  const age = nowMs - new Date(enqueuedAt).getTime();
  return age >= GUEST_MONKEY_SLIP_TTL_MS;
}
